import { useCallback, useEffect, useMemo, useState } from "react";
import {
  assignDeviceToAccount,
  createAccountDevice,
  deassignAccountDevice,
  deleteAccountDevice,
  listAccountDevices,
  listUnassignedDevices,
  updateAccountDevice,
} from "../../api/endpoints/accountDevices.js";
import { listDeviceTypes } from "../../api/endpoints/deviceTypes.js";
import { listSims } from "../../api/endpoints/sims.js";
import { buildDeviceListSubtitle } from "../../pages/Devices/components/deviceUtils.js";

export function useAccountDevices(accountId) {
  const [items, setItems] = useState([]);
  const [unassignedDevices, setUnassignedDevices] = useState([]);
  const [deviceTypes, setDeviceTypes] = useState([]);
  const [sims, setSims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [saving, setSaving] = useState(false);

  const deviceTypeMap = useMemo(
    () => Object.fromEntries(deviceTypes.map((type) => [type.id, type])),
    [deviceTypes],
  );

  const simMap = useMemo(
    () => Object.fromEntries(sims.map((sim) => [sim.id, sim])),
    [sims],
  );

  const reload = useCallback(async () => {
    if (!accountId) {
      setItems([]);
      setUnassignedDevices([]);
      setDeviceTypes([]);
      setSims([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [data, pool, types, simRows] = await Promise.all([
        listAccountDevices(accountId),
        listUnassignedDevices(),
        listDeviceTypes(),
        listSims(),
      ]);
      setItems(data);
      setUnassignedDevices(pool);
      setDeviceTypes(types);
      setSims(simRows);
      setSelectedId((prev) => {
        if (prev && data.some((d) => d.id === prev)) return prev;
        return data[0]?.id ?? null;
      });
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => {
    reload();
  }, [reload]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((d) => {
      const typeName = deviceTypeMap[d.deviceTypeId]?.name ?? d.deviceType ?? "";
      const sim = simMap[d.simId];
      const valueText = Object.values(d.values ?? {}).join(" ");
      return [d.deviceId, typeName, d.simType, d.simNumber, sim?.iccid, sim?.msisdn, valueText]
        .some((v) => String(v ?? "").toLowerCase().includes(q));
    });
  }, [items, search, deviceTypeMap, simMap]);

  useEffect(() => {
    if (visible.length === 0) {
      setSelectedId(null);
      return;
    }
    if (!visible.some((d) => d.id === selectedId)) {
      setSelectedId(visible[0].id);
    }
  }, [visible, selectedId]);

  const selected = useMemo(
    () => items.find((d) => d.id === selectedId) ?? null,
    [items, selectedId],
  );

  const selectedDeviceType = useMemo(
    () => (selected?.deviceTypeId ? deviceTypeMap[selected.deviceTypeId] ?? null : null),
    [selected, deviceTypeMap],
  );

  const selectedSim = useMemo(
    () => (selected?.simId ? simMap[selected.simId] ?? null : null),
    [selected, simMap],
  );

  const addDevice = useCallback(async (payload) => {
    setSaving(true);
    try {
      const created = await createAccountDevice(accountId, payload);
      setItems((list) => [created, ...list]);
      setSelectedId(created.id);
      return created;
    } finally {
      setSaving(false);
    }
  }, [accountId]);

  const saveDevice = useCallback(async (id, payload) => {
    setSaving(true);
    try {
      const updated = await updateAccountDevice(accountId, id, payload);
      setItems((list) => list.map((d) => (d.id === id ? updated : d)));
      return updated;
    } finally {
      setSaving(false);
    }
  }, [accountId]);

  const removeDevice = useCallback(async (id) => {
    await deleteAccountDevice(accountId, id);
    setItems((list) => list.filter((d) => d.id !== id));
  }, [accountId]);

  const deassignDevice = useCallback(async (id) => {
    setSaving(true);
    try {
      await deassignAccountDevice(accountId, id);
      setItems((list) => list.filter((d) => d.id !== id));
      setUnassignedDevices(await listUnassignedDevices());
    } finally {
      setSaving(false);
    }
  }, [accountId]);

  const assignExistingDevice = useCallback(async (deviceId) => {
    setSaving(true);
    try {
      const updated = await assignDeviceToAccount(deviceId, accountId);
      setItems((list) => [updated, ...list]);
      setUnassignedDevices((pool) => pool.filter((d) => d.id !== deviceId));
      setSelectedId(updated.id);
      return updated;
    } finally {
      setSaving(false);
    }
  }, [accountId]);

  const getDeviceTypeName = useCallback(
    (deviceTypeId) => deviceTypeMap[deviceTypeId]?.name ?? "—",
    [deviceTypeMap],
  );

  const getDeviceSubtitle = useCallback(
    (device) => buildDeviceListSubtitle(device, deviceTypeMap[device?.deviceTypeId]),
    [deviceTypeMap],
  );

  return {
    items: visible,
    allItems: items,
    unassignedDevices,
    deviceTypes,
    selectedDeviceType,
    sims,
    selectedSim,
    loading,
    search,
    setSearch,
    selected,
    selectedId,
    setSelectedId,
    addDevice,
    saveDevice,
    removeDevice,
    deassignDevice,
    assignExistingDevice,
    saving,
    reload,
    getDeviceTypeName,
    getDeviceSubtitle,
  };
}
